/**
 * Ícono del ORIGEN de un pedido — el campo `package.type` (MERCADOLIBRE,
 * SHOPIFY, OTS, EXCEL…), o sea por qué canal entró el pedido al sistema.
 *
 * Por qué existe un componente y no otro mapa suelto: el repo ya arrastra
 * cuatro catálogos de origen duplicados (ModernOrdersList, evidenceView,
 * deliveryReportPdf, downloadVoucher) que ya divergieron entre sí — a ninguno
 * le llegó TiendaNube, y un pedido sin `type` se anuncia "Desconocido" en el
 * listado, "Manual" en el detalle y vacío en el PDF. Este componente es el
 * quinto consumidor del dato, así que se escribe una vez y se reusa; unificar
 * los otros cuatro es trabajo aparte y no se hace de paso acá.
 *
 * Tres cosas que este dato exige y que no son evidentes:
 *
 *  1. El casing viene sucio de la colección: `MERCADOLIBRE` y `MercadoLibre`
 *     conviven, igual que `EXCEL`/`Excel` y `Prestashop`/`PrestaShop`. Por eso
 *     el catálogo se indexa en MAYÚSCULAS y el lookup normaliza. Comparar
 *     contra el enum del schema no sirve: ese enum no es el catálogo real.
 *
 *  2. Hay documentos históricos SIN `type`, y no hay backfill. Esos NO se
 *     etiquetan "Manual" (sería inventar un origen): caen al ícono genérico
 *     declarando que el origen no está registrado.
 *
 *  3. El modo oscuro invierte por defecto TODO `img`/`svg` de la app
 *     (_dark-mode-overrides.scss). Un logo de marca invertido queda
 *     irreconocible, así que los `<img>` de acá llevan `dark-safe-img`, que es
 *     la clase de escape establecida en el proyecto.
 */
import { iconList } from '../utils/iconList';

// Origen → etiqueta + ícono. Claves SIEMPRE en mayúsculas (ver punto 1 arriba).
//
// TIENDANUBE se incluye aunque hoy ningún otro mapa lo tenga: el asset existe
// (`iconList.tiendanube`) y la integración está publicada, así que el pedido ya
// puede llegar. El valor exacto que persiste esa integración no está verificado
// contra la colección — si resultara ser otro, el pedido cae al genérico con su
// `type` crudo a la vista, que es exactamente lo que hace falta para detectarlo.
const ORIGIN_META = {
  MERCADOLIBRE: { label: 'MercadoLibre', icon: iconList.ml },
  SHOPIFY: { label: 'Shopify', icon: iconList.shopify },
  JUMPSELLER: { label: 'Jumpseller', icon: iconList.jumpsellerIcon },
  FALABELLA: { label: 'Falabella', icon: iconList.falabella },
  'FALABELLA DIRECTO': { label: 'Falabella Directo', icon: iconList.falabella },
  RIPLEY: { label: 'Ripley', icon: iconList.ripley },
  WOOCOMMERCE: { label: 'WooCommerce', icon: iconList.woo },
  PRESTASHOP: { label: 'Prestashop', icon: iconList.prestashop },
  TIENDANUBE: { label: 'Tienda Nube', icon: iconList.tiendanube },
  // El backend persiste `package.type` = 'Wix'; la clave va en MAYÚSCULAS
  // porque el lookup normaliza con originKey() (ver punto 1 arriba).
  WIX: { label: 'Wix', icon: iconList.wix },
  PARIS: { label: 'Paris', icon: iconList.paris },
  SENDU: { label: 'Sendu', icon: iconList.sendu },
  OTS: { label: 'Manual', icon: iconList.box },
  // Novapet está en el enum del back y en el filtro de Pedidos, pero no tiene
  // asset de logo en el repo: va al genérico con su nombre bien escrito, que es
  // mejor que dejar que caiga al fallback y se anuncie en mayúsculas.
  NOVAPET: { label: 'Novapet', icon: iconList.box },
  // Excel no tiene logo de marca: se dibuja con Font Awesome, igual que en el
  // listado de Pedidos y en el detalle. Font Awesome está bundleado global.
  EXCEL: { label: 'Excel', excel: true },
};

/** Verde corporativo de Excel — el mismo que ya usan listado y detalle. */
const EXCEL_GREEN = '#1D6F42';

/**
 * Clave canónica de un `package.type`, o `null` si el pedido no tiene origen.
 *
 * El casing del campo viene sucio de la colección — en `packages` conviven
 * 'MercadoLibre', 'MERCADOLIBRE' y 'mercadolibre', y nunca se corrió un backfill
 * — así que comparar con `===` contra un literal falla en silencio para parte de
 * los datos. Toda comparación de origen debe pasar por acá, no por el string
 * crudo, y las claves de `ORIGIN_META` son ya el resultado de esta función.
 */
export function originKey(type) {
  const raw = String(type ?? '').trim();
  return raw ? raw.toUpperCase() : null;
}

/**
 * Etiqueta legible del origen de un pedido.
 *
 * Un `type` que no está en el catálogo se devuelve CRUDO en vez de colapsar a
 * "Desconocido": si mañana entra un origen nuevo, es preferible que el operador
 * lea "Novapet" a que lea una etiqueta genérica que esconde el dato.
 */
export function originLabel(type, { fallback = 'Sin origen registrado' } = {}) {
  const key = originKey(type);
  if (!key) return fallback;
  return ORIGIN_META[key]?.label ?? String(type).trim();
}

/**
 * @param decorative  Fuerza el ícono a decorativo: sin nombre accesible ni
 *   tooltip. Se usa donde la etiqueta ya está escrita al lado — repetirla haría
 *   que el lector de pantalla dijera el mismo dato dos veces en la misma línea.
 *
 * Un paquete SIN origen también sale decorativo, aunque no se pida: ahí el
 * ícono es un relleno para que la columna no quede despareja, y anunciarlo
 * ("sin origen registrado") una vez por fila convertiría una lista de 40
 * paradas en 40 avisos de que falta un dato.
 */
export function OriginIcon({ type, size = 16, className, decorative = false }) {
  const key = originKey(type);
  const meta = key ? ORIGIN_META[key] : undefined;
  const label = originLabel(type);
  const muted = decorative || !key;

  if (meta?.excel) {
    return (
      <i
        className={`fas fa-file-excel shrink-0 ${className ?? ''}`}
        style={{ fontSize: size, lineHeight: 1, color: EXCEL_GREEN }}
        title={muted ? undefined : label}
        role={muted ? undefined : 'img'}
        aria-hidden={muted ? 'true' : undefined}
        aria-label={muted ? undefined : label}
      />
    );
  }

  return (
    <img
      src={meta?.icon ?? iconList.box}
      // Con nombre accesible cuando el ícono ES el dato: en la lista de paradas
      // el origen no está escrito en ningún otro lado.
      alt={muted ? '' : label}
      title={muted ? undefined : label}
      aria-hidden={muted ? 'true' : undefined}
      className={`dark-safe-img shrink-0 ${className ?? ''}`}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

export default OriginIcon;
